<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else>
    <MainHeader :title="'Net Worth'">
      <template #right>
        <div
          class="help-beacon"
          @click="openBeacon"
          title="Get Help"
          role="button"
          aria-label="Get Help"
        >
          <font-awesome-icon
            :icon="['fas', 'question']"
            class="help-beacon-icon"
          />
        </div>
      </template>
    </MainHeader>

    <div class="view nw-view content-padding">
      <div class="nw-total-card">
        <span class="nw-total-label">Total Net Worth</span>
        <p class="nw-total-value">
          {{ formatMoney(accountsList.netWorthTotal) }}
        </p>
      </div>

      <div class="chart-wrapper mt-2 mb-2 mx-auto">
        <canvas ref="netWorthChart" class="netWorthChart"></canvas>
      </div>

      <div class="asset-liability-grid">
        <div class="overview-card">
          <div class="text">Total Assets</div>
          <div class="value">{{ formatMoney(accountsList.totalAssets) }}</div>
        </div>
        <div class="overview-card">
          <div class="text">Total Liabilities</div>
          <div class="value">
            {{ formatMoney(accountsList.totalLiabilities) }}
          </div>
        </div>
      </div>

      <div class="et-tabs mt-2">
        <button
          class="et-tab"
          :class="{ active: assetsSliderSelected }"
          @click="assetsSliderSelected = true"
        >
          Assets
        </button>
        <button
          class="et-tab"
          :class="{ active: !assetsSliderSelected }"
          @click="assetsSliderSelected = false"
        >
          Liabilities
        </button>
      </div>

      <div v-if="assetsSliderSelected">
        <div class="chart-wrapper mx-auto mt-2 mb-2">
          <canvas ref="assetAccountsChart"></canvas>
        </div>
        <div class="mt-2 mb-2">
          <span class="section-label">Asset accounts</span>
          <div v-if="assetAccounts && assetAccounts.length > 0">
            <div
              v-for="account in assetAccounts"
              :key="account"
              class="account-card mb-1"
              @click="editAccount(account)"
            >
              <div class="account-icon-wrap">
                <font-awesome-icon
                  :icon="
                    account.icon
                      ? parseIconString(account.icon)
                      : ['fas', 'building-columns']
                  "
                  :style="{ color: account.iconColor || '#7F77DD' }"
                  style="font-size: 15px"
                />
              </div>
              <div class="account-info">
                <div class="account-name">
                  {{ account.displayName || account.name }}
                </div>
                <div class="account-type">
                  {{ account.institutionName || account.type }}
                </div>
              </div>
              <div class="account-balance">
                {{ formatMoney(account.currentBalance) }}
              </div>
            </div>
          </div>
          <EmptyState
            v-else
            title="No asset accounts"
            subtitle="Add your first asset account to start tracking your net worth."
          >
            <template #icon>
              <svg viewBox="0 0 72 72" fill="none">
                <rect
                  x="4"
                  y="4"
                  width="64"
                  height="64"
                  rx="16"
                  fill="#E1F5EE"
                />
                <rect
                  x="18"
                  y="32"
                  width="36"
                  height="20"
                  rx="4"
                  stroke="#5DCAA5"
                  stroke-width="2"
                />
                <path
                  d="M24 32v-4a12 12 0 0124 0v4"
                  stroke="#5DCAA5"
                  stroke-width="2"
                  stroke-linecap="round"
                />
                <circle cx="36" cy="42" r="3" fill="#5DCAA5" />
              </svg>
            </template>
            <template #actions>
              <button
                class="gradient-button w-100"
                @click="router.push({ name: 'AddAccount' })"
              >
                + Add Account
              </button>
            </template>
          </EmptyState>
        </div>
        <button
          class="teal-button w-100 mb-2"
          @click="router.push({ name: 'AddAccount' })"
        >
          + Add Account
        </button>
      </div>

      <div v-else>
        <div class="chart-wrapper mx-auto mt-2 mb-2">
          <canvas ref="liabilityAccountsChart"></canvas>
        </div>
        <div class="mt-2 mb-2">
          <span class="section-label">Liability accounts</span>
          <div v-if="liabilityAccounts && liabilityAccounts.length > 0">
            <div
              v-for="account in liabilityAccounts"
              :key="account"
              class="account-card mb-1"
              @click="editAccount(account)"
            >
              <div class="account-icon-wrap">
                <font-awesome-icon
                  :icon="
                    account.icon
                      ? parseIconString(account.icon)
                      : ['fas', 'credit-card']
                  "
                  :style="{ color: account.iconColor || '#D85A30' }"
                  style="font-size: 15px"
                />
              </div>
              <div class="account-info">
                <div class="account-name">
                  {{ account.displayName || account.name }}
                </div>
                <div class="account-type">
                  {{ account.institutionName || account.type }}
                </div>
              </div>
              <div class="account-balance">
                {{ formatMoney(account.currentBalance) }}
              </div>
            </div>
          </div>
          <EmptyState
            v-else
            title="No liability accounts"
            subtitle="Add loans, credit cards, or mortgages to track what you owe."
          >
            <template #icon>
              <svg viewBox="0 0 72 72" fill="none">
                <rect
                  x="4"
                  y="4"
                  width="64"
                  height="64"
                  rx="16"
                  fill="#EEEDFE"
                />
                <rect
                  x="16"
                  y="26"
                  width="40"
                  height="26"
                  rx="5"
                  stroke="#7F77DD"
                  stroke-width="2"
                />
                <path d="M16 34h40" stroke="#7F77DD" stroke-width="2" />
                <rect
                  x="22"
                  y="40"
                  width="10"
                  height="4"
                  rx="2"
                  fill="#7F77DD"
                  opacity="0.5"
                />
              </svg>
            </template>
            <template #actions>
              <button
                class="gradient-button w-100"
                @click="router.push({ name: 'AddAccount' })"
              >
                + Add Account
              </button>
            </template>
          </EmptyState>
        </div>
        <button
          class="teal-button w-100 mb-2"
          @click="router.push({ name: 'AddAccount' })"
        >
          + Add Account
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import MainHeader from '../components/View-Header.vue';
import EmptyState from '../components/EmptyState.vue';
import { formatMoney } from '../utils/formatMoney';
import SkeletonLoader from '../components/SkeletonLoader.vue';
import {
  Chart,
  DoughnutController,
  LineController,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from 'chart.js';
Chart.register(
  DoughnutController,
  LineController,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale
);

export default {
  components: { MainHeader, SkeletonLoader, EmptyState },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    let assetsSliderSelected = ref(true);
    const netWorthChart = ref(null);
    const netWorthData = ref([]);
    let accountsList = ref([]);
    let assetAccounts = ref([]);
    let liabilityAccounts = ref([]);
    let assetAccountsChart = ref(null);
    let assetDoughnutChart = ref(null);
    let liabilityAccountsChart = ref(null);
    let liabilityDoughnutChart = ref(null);
    const loading = ref(true);

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);

      store
        .getNetWorthDataPoints()
        .then((dataPointsResponse) => {
          if (
            dataPointsResponse.data &&
            Array.isArray(dataPointsResponse.data)
          ) {
            netWorthData.value = dataPointsResponse.data.map((item) => ({
              date: item.date,
              value: item.value,
            }));
          }
          return store.getNetWorth();
        })
        .then((response) => {
          loading.value = false;
          accountsList.value = response.data;
          assetAccounts.value = response.data.assetAccounts;
          liabilityAccounts.value = response.data.liabilityAccounts;
          nextTick(() => {
            createNetWorthChart();
            createAssetsDoughnutChart();
          });
        })
        .catch((error) => {
          console.error('Error loading data:', error);
          loading.value = false;
        });
    });

    watch(assetsSliderSelected, (newValue) => {
      if (!newValue) {
        nextTick(() => {
          createLiabilitiesDoughnutChart();
        });
      } else {
        nextTick(() => {
          createAssetsDoughnutChart();
        });
      }
    });

    function createNetWorthChart() {
      if (!netWorthChart.value) return;
      if (netWorthChart.value._chart) netWorthChart.value._chart.destroy();

      const ctx = netWorthChart.value.getContext('2d');

      if (netWorthData.value.length === 0) {
        netWorthChart.value._chart = new Chart(ctx, {
          type: 'line',
          data: { labels: [], datasets: [{ data: [] }] },
          options: {
            plugins: {
              title: {
                display: true,
                text: 'Net Worth Over Time',
                font: { size: 14, family: 'Poppins' },
              },
              legend: { display: false },
            },
          },
          plugins: [noDataPlugin],
        });
        return;
      }

      const dates = netWorthData.value.map((d) => d.date);
      const values = netWorthData.value.map((d) => d.value);
      netWorthChart.value._chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: dates,
          datasets: [
            {
              data: values,
              borderColor: '#5DCAA5',
              borderWidth: 2.5,
              pointRadius: 4,
              pointBackgroundColor: '#5DCAA5',
              pointBorderColor: 'rgba(255,255,255,1)',
              fill: true,
              backgroundColor: 'rgba(93,202,165,0.08)',
            },
          ],
        },
        options: {
          plugins: {
            legend: { display: false },
            title: {
              display: true,
              text: 'Net Worth Over Time',
              font: { size: 14, family: 'Poppins' },
            },
          },
          animations: {
            tension: {
              duration: 3000,
              easing: 'linear',
              from: 1.5,
              to: 0.5,
              loop: false,
            },
          },
        },
      });
    }

    const noDataPlugin = {
      id: 'noData',
      afterDraw: (chart) => {
        const hasData = chart.data.datasets.some((d) =>
          d.data.some((v) => v !== 0 && v !== null && v !== undefined)
        );
        if (!hasData) {
          const {
            ctx,
            chartArea: { left, top, width, height },
          } = chart;
          ctx.save();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = '14px Poppins, Arial';
          ctx.fillStyle = store.darkTheme
            ? 'rgba(100,100,100,0.8)'
            : 'rgba(0,0,0,0.4)';
          ctx.fillText('No data available', left + width / 2, top + height / 2);
          ctx.restore();
        }
      },
    };

    function createAssetsDoughnutChart() {
      if (assetDoughnutChart.value) assetDoughnutChart.value.destroy();
      const data = assetAccounts.value.map((a) => ({
        label: a.displayName || a.name || 'Unnamed',
        amount: a.currentBalance || 0,
        color: a.iconColor || '#7F77DD',
      }));
      assetDoughnutChart.value = new Chart(assetAccountsChart.value, {
        type: 'doughnut',
        data: {
          labels: data.map((i) => i.label),
          datasets: [
            {
              data: data.map((i) => i.amount),
              backgroundColor: data.map((i) => i.color),
            },
          ],
        },
        options: {
          plugins: {
            legend: { position: 'top' },
            title: {
              display: true,
              text: 'Asset Accounts',
              font: { size: 14, family: 'Poppins' },
            },
          },
        },
        plugins: [noDataPlugin],
      });
    }

    function createLiabilitiesDoughnutChart() {
      if (liabilityDoughnutChart.value) liabilityDoughnutChart.value.destroy();
      const data = liabilityAccounts.value.map((a) => ({
        label: a.displayName || a.name || 'Unnamed',
        amount: a.currentBalance || 0,
        color: a.iconColor || '#D85A30',
      }));
      liabilityDoughnutChart.value = new Chart(liabilityAccountsChart.value, {
        type: 'doughnut',
        data: {
          labels: data.map((i) => i.label),
          datasets: [
            {
              data: data.map((i) => i.amount),
              backgroundColor: data.map((i) => i.color),
            },
          ],
        },
        options: {
          plugins: {
            legend: { position: 'top' },
            title: {
              display: true,
              text: 'Liability Accounts',
              font: { size: 14, family: 'Poppins' },
            },
          },
        },
        plugins: [noDataPlugin],
      });
    }

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const editAccount = (account) => {
      store.editAccount(account);
      router.push({
        name: 'EditAccount',
        params: { previousPage: 'NetWorth' },
      });
    };

    onBeforeUnmount(() => {
      window.Beacon('close');
    });

    const openBeacon = () => {
      if (window.Beacon && typeof window.Beacon === 'function') {
        window.Beacon('on', 'ready', () => {
          window.Beacon('open');
          window.Beacon('navigate', '/docs/search?query=Net%20Worth');
        });
        window.Beacon('open');
        window.Beacon('navigate', '/docs/search?query=Net%20Worth');
      } else {
        console.error('Beacon is not initialized');
      }
    };

    return {
      store,
      router,
      assetsSliderSelected,
      netWorthChart,
      netWorthData,
      assetAccounts,
      liabilityAccounts,
      assetAccountsChart,
      assetDoughnutChart,
      liabilityAccountsChart,
      liabilityDoughnutChart,
      formatMoney,
      accountsList,
      parseIconString,
      editAccount,
      loading,
      openBeacon,
    };
  },
};
</script>

<style scoped>
.nw-view {
  animation: viewFadeIn 0.4s ease both;
}

.netWorthChart {
  padding: 1em 0;
}

.help-beacon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--gradient-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--shadow-purple);
  transition:
    transform var(--transition-fast),
    box-shadow var(--transition-fast);
}
.help-beacon:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-purple-lg);
}
.help-beacon-icon {
  color: white;
  font-size: 13px;
}

.asset-liability-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 0.5rem;
}

.chart-wrapper {
  max-width: 420px;
  width: 100%;
}

.et-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 1rem;
  background: #f0f0f4;
  border-radius: var(--radius-pill);
  padding: 4px;
}
.dark-theme .et-tabs {
  background: #1a2535;
}

.et-tab {
  border: none;
  border-radius: var(--radius-pill);
  padding: 0.6rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: #888;
  transition: all 0.2s ease;
}
.et-tab.active {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
  box-shadow: 0 4px 12px rgba(127, 119, 221, 0.3);
}

.account-card {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 10px;
  background: white;
  border-radius: var(--radius-md);
  border: 1px solid #f0f0f0;
  box-shadow: var(--shadow-sm);
  padding: 0.85rem 1rem;
  cursor: pointer;
  transition:
    transform var(--transition-base),
    box-shadow var(--transition-base);
  animation: cardSlideUp 0.35s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}
.dark-theme .account-card {
  background: #212e3e;
  border-color: #2a3a4e;
}
.account-card:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}

.account-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f4f4f8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.dark-theme .account-icon-wrap {
  background: #1a2535;
}

.account-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.account-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #213547;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dark-theme .account-name {
  color: white;
}
.account-type {
  font-size: 0.68rem;
  color: #aaa;
}

.account-balance {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--color-primary);
  text-align: right;
  white-space: nowrap;
}
.dark-theme .account-balance {
  color: #d4d2d2;
}

.icon-wrapper svg {
  display: block;
  position: relative;
  line-height: 30px;
}

@media screen and (min-width: 992px) {
  .chart-wrapper {
    max-width: 500px;
  }
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader />
  </div>
  <div v-else>
    <div>
      <MainHeader :title="'Net Worth'">
        <template #right>
          <div class="beacon-launcher" @click="openBeacon" title="Get Help">
            <font-awesome-icon
              :icon="['fas', 'question']"
              class="beacon-icon"
            />
          </div>
        </template>
      </MainHeader>
    </div>
    <div class="view">
      <div class="content-padding">
        <div class="overview-card">
          <div class="text">Total Net Worth</div>
          <div class="value">{{ formatMoney(accountsList.netWorthTotal) }}</div>
        </div>
        <div class="mt-4 mb-4 asset-overview-chart-wrapper">
          <canvas ref="netWorthChart"></canvas>
        </div>
        <div class="asset-liability-overview">
          <div class="overview-card">
            <div class="text">Total Assets</div>
            <div class="value">{{ formatMoney(accountsList.totalAssets) }}</div>
          </div>
          <div class="overview-card">
            <div class="text">Total Liabilities</div>
            <div class="value">
              {{ formatMoney(accountsList.totalLiabilities) }}
            </div>
          </div>
        </div>
        <div class="mt-4">
          <Slider
            :options="sliderOptions"
            @sliderSelection="sliderOptionUpdated($event)"
          ></Slider>
        </div>
        <div v-if="assetsSliderSelected" class="assets">
          <div class="mt-3 pb-2 asset-chart-wrapper">
            <canvas ref="assetAccountsChart"></canvas>
          </div>
          <div class="mt-4 mb-2">
            <div
              v-for="assetAccount in assetAccounts"
              :key="assetAccount"
              class="mb-2"
            >
              <div
                class="accounts-list align-center pointer"
                @click="editAccount(assetAccount)"
              >
                <div class="icon">
                  <span class="icon-wrapper">
                    <font-awesome-icon
                      :icon="
                        assetAccount.icon
                          ? parseIconString(assetAccount.icon)
                          : ['fas', 'user']
                      "
                      :style="{ color: assetAccount.iconColor }"
                    ></font-awesome-icon
                  ></span>
                </div>
                <div class="category-label">
                  {{
                    assetAccount.displayName
                      ? assetAccount.displayName
                      : assetAccount.name
                  }}
                </div>
                <div class="balance">
                  {{ formatMoney(assetAccount.currentBalance) }}
                </div>
              </div>
            </div>
            <div>
              <button
                class="blue-button mb-2 w-100"
                @click="router.push({ name: 'AddAccount' })"
              >
                Add Account
              </button>
            </div>
          </div>
        </div>
        <div v-else>
          <div class="mt-3 pb-2 liability-chart-wrapper">
            <canvas ref="liabilityAccountsChart"></canvas>
          </div>
          <div class="mt-4 mb-2">
            <div
              v-for="liabilityAccount in liabilityAccounts"
              :key="liabilityAccount"
              class="mb-2"
            >
              <div
                class="accounts-list align-center pointer"
                @click="editAccount(liabilityAccount)"
              >
                <div class="icon">
                  <span class="icon-wrapper">
                    <font-awesome-icon
                      :icon="
                        liabilityAccount.icon
                          ? parseIconString(liabilityAccount.icon)
                          : ['fas', 'user']
                      "
                      :style="{ color: liabilityAccount.iconColor }"
                    ></font-awesome-icon
                  ></span>
                </div>
                <div class="category-label">
                  {{
                    liabilityAccount.displayName
                      ? liabilityAccount.displayName
                      : liabilityAccount.name
                  }}
                </div>
                <div class="balance">
                  {{ formatMoney(liabilityAccount.currentBalance) }}
                </div>
              </div>
            </div>
            <div>
              <button
                class="blue-button mb-2 w-100"
                @click="router.push({ name: 'AddAccount' })"
              >
                Add Account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick, onBeforeUnmount } from 'vue';
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';

import MainHeader from '../components/View-Header.vue';
import Slider from '../components/Slider.vue';
import { NetWorth_Types } from '../utils/constants.js';
import { formatMoney } from '../utils/formatMoney';
import SkeletonLoader from '../components/SkeletonLoader.vue';
import {
  Chart,
  DoughnutController,
  LineController,
  PointElement,
  LineElement,
} from 'chart.js';
Chart.register(DoughnutController, LineController, PointElement, LineElement);

export default {
  components: { MainHeader, Slider, SkeletonLoader },
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const sliderOptions = [
      { label: 'Assets', disabled: false },
      { label: 'Liabilities', disabled: true },
    ];
    let assetsSliderSelected = ref(true);
    const netWorthChart = ref(null);
    const netWorthData = ref([]);
    let accountsList = ref([]);
    let assetAccounts = ref([]);
    let liabilityAccounts = ref([]);
    let assetAccountsChart = ref(null);
    let assetDoughnutChart = ref(null);
    let liabilityAccountsChart = ref(null);
    let liabilityDoughnutChart = ref(null);
    const linkToken = ref('');
    const loading = ref(true);

    const sliderOptionUpdated = (selection) => {
      if (selection === NetWorth_Types.assets) {
        assetsSliderSelected.value = true;
      } else {
        assetsSliderSelected.value = false;
      }
    };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      store
        .getNetWorthDataPoints()
        .then((dataPointsResponse) => {
          if (
            dataPointsResponse.data &&
            Array.isArray(dataPointsResponse.data)
          ) {
            netWorthData.value = dataPointsResponse.data.map((item) => ({
              date: item.date,
              value: item.value,
            }));
          }

          return store.getNetWorth();
        })
        .then((response) => {
          loading.value = false;
          accountsList.value = response.data;
          assetAccounts.value = response.data.assetAccounts;
          liabilityAccounts.value = response.data.liabilityAccounts;

          nextTick(() => {
            createNetWorthChart();
            createAssetsDoughnutChart();
          });
        })
        .catch((error) => {
          console.error('Error loading data:', error);
          loading.value = false;
        });
    });

    watch(assetsSliderSelected, (newValue) => {
      if (!newValue) {
        nextTick(() => {
          createLiabilitiesDoughnutChart();
        });
      } else {
        nextTick(() => {
          createAssetsDoughnutChart();
        });
      }
    });

    function createNetWorthChart() {
      if (netWorthData.value.length === 0) {
        console.warn('No net worth data points available');
        return;
      }

      const dates = netWorthData.value.map((data) => data.date);
      const values = netWorthData.value.map((data) => data.value);

      if (netWorthChart.value._chart) {
        netWorthChart.value._chart.destroy();
      }

      const ctx = netWorthChart.value.getContext('2d');
      netWorthChart.value._chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: dates,
          datasets: [
            {
              data: values,
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 2,
              pointRadius: 4,
              pointBorderColor: 'rgba(255, 255, 255, 1)',
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
            title: {
              display: true,
              text: 'Net Worth Over Time',
              font: {
                size: 15,
              },
            },
          },
          animations: {
            tension: {
              duration: 3000,
              easing: 'linear',
              from: 1.5,
              to: 0.5,
              loop: false,
            },
          },
        },
      });
    }

    const noDataPlugin = {
      id: 'noData',
      afterDraw: (chart) => {
        const datasets = chart.data.datasets;
        let hasData = false;

        for (let i = 0; i < datasets.length; i++) {
          if (
            datasets[i].data.some(
              (data) => data !== 0 && data !== null && data !== undefined
            )
          ) {
            hasData = true;
            break;
          }
        }

        if (!hasData) {
          const {
            ctx,
            chartArea: { left, top, width, height },
          } = chart;
          ctx.save();
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.font = '16px Arial';
          ctx.fillStyle = store.darkTheme
            ? 'rgba(100, 100, 100, 0.5)'
            : 'rgba(0, 0, 0, 0.5)';
          ctx.fillText('No data available', left + width / 2, top + height / 2);
          ctx.restore();
        }
      },
    };

    function createAssetsDoughnutChart() {
      if (assetDoughnutChart.value) {
        assetDoughnutChart.value.destroy();
      }
      const assetsGraphData = [];
      assetAccounts.value.forEach((account) => {
        let accountData = {
          categoryLabel: account.name ? account.displayName : 'Uncategorized',
          amount: account.currentBalance,
          backgroundColor: account.iconColor,
        };
        assetsGraphData.push(accountData);
      });
      assetDoughnutChart.value = new Chart(assetAccountsChart.value, {
        type: 'doughnut',
        data: {
          labels: assetsGraphData.map((item) => item.categoryLabel),
          datasets: [
            {
              data: assetsGraphData.map((item) =>
                item.amount ? item.amount : 0
              ),
              backgroundColor: assetsGraphData.map(
                (item) => item.backgroundColor
              ),
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              position: 'top',
            },
            title: {
              display: true,
              text: 'Asset Accounts',
              font: {
                size: '26',
              },
            },
          },
        },
        plugins: [noDataPlugin],
      });
    }

    function createLiabilitiesDoughnutChart() {
      if (liabilityDoughnutChart.value) {
        liabilityDoughnutChart.value.destroy();
      }
      const assetsGraphData = [];
      liabilityAccounts.value.forEach((account) => {
        let accountData = {
          categoryLabel: account.name ? account.displayName : 'Uncategorized',
          amount: account.currentBalance,
          backgroundColor: account.iconColor,
        };
        assetsGraphData.push(accountData);
      });
      liabilityDoughnutChart.value = new Chart(liabilityAccountsChart.value, {
        type: 'doughnut',
        data: {
          labels: assetsGraphData.map((item) => item.categoryLabel),
          datasets: [
            {
              data: assetsGraphData.map((item) =>
                item.amount ? item.amount : 0
              ),
              backgroundColor: assetsGraphData.map(
                (item) => item.backgroundColor
              ),
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              position: 'top',
            },
            title: {
              display: true,
              text: 'Asset Accounts',
              font: {
                size: '26',
              },
            },
          },
        },
        plugins: [noDataPlugin],
      });
    }

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const editAccount = (account) => {
      store.editAccount(account);
      router.push({
        name: 'EditAccount',
        params: { previousPage: 'NetWorth' },
      });
    };

    onBeforeUnmount(() => {
      window.Beacon('close');
    });

    const openBeacon = () => {
      if (window.Beacon && typeof window.Beacon === 'function') {
        window.Beacon('on', 'ready', () => {
          window.Beacon('open');
          window.Beacon('navigate', '/docs/search?query=Net%20Worth');
        });
        window.Beacon('open');
        window.Beacon('navigate', '/docs/search?query=Net%20Worth');
      } else {
        console.error('Beacon is not initialized');
      }
    };

    return {
      store,
      router,
      sliderOptions,
      sliderOptionUpdated,
      assetsSliderSelected,
      netWorthChart,
      createNetWorthChart,
      assetAccounts,
      liabilityAccounts,
      createAssetsDoughnutChart,
      createLiabilitiesDoughnutChart,
      formatMoney,
      accountsList,
      parseIconString,
      assetDoughnutChart,
      assetAccountsChart,
      liabilityDoughnutChart,
      liabilityAccountsChart,
      linkToken,
      editAccount,
      loading,
      openBeacon,
    };
  },
};
</script>
<style scoped>
.asset-liability-overview {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5em;
}

.accounts-list {
  display: grid;
  grid-template-columns: 15% 65% 20%;
  box-shadow: 0px 3px 20px rgba(0, 0, 0, 0.1);
  padding: 1em;
  border-radius: 5px;
}

.light-theme .accounts-list {
  background: white;
}

.dark-theme .accounts-list {
  background: #212e3e;
  color: white;
}

.accounts-list .balance {
  font-weight: 500;
  font-size: clamp(0.75em, 1.8vw, 0.9em);
  text-align: right;
}

.accounts-list .last-updated {
  font-size: 0.8em;
  grid-column: 2 / span 3;
  text-align: left;
}

.light-theme .accounts-list .last-updated {
  color: #3a3d41;
}
.dark-theme .accounts-list .last-updated {
  color: #d4d2d2;
}
.icon-wrapper svg {
  display: block;
  position: relative;
  text-align: center;
  line-height: 30px;
}

.asset-overview-chart-wrapper {
  max-width: 700px;
  margin: 2em auto;
}

.asset-chart-wrapper,
.liability-chart-wrapper {
  max-width: 500px;
  margin: 2em auto;
}
</style> -->
